import { Modal } from "antd";
import { checkInQRTokenApi, verifyQRTokenApi } from "api/services/jobs";
import { format, parseISO } from "date-fns";
import { Html5Qrcode } from "html5-qrcode";
import { useEffect, useRef, useState } from "react";
import { Bounce, toast } from "react-toastify";

const QRScanner = ({ open, onCancel, onScanSuccess }) => {
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [error, setError] = useState(null);
  const [manualInputValue, setManualInputValue] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const html5QrCodeRef = useRef(null);
  const scannerIdRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      startScanner();
    } else {
      stopScanner();
      setScannedData(null);
      setError(null);
      setManualInputValue("");
      // Clear input field
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
    return () => {
      stopScanner();
    };
  }, [open]);

  const startScanner = async () => {
    try {
      // Clear any existing scanner
      if (html5QrCodeRef.current) {
        try {
          await html5QrCodeRef.current.stop();
          await html5QrCodeRef.current.clear();
        } catch (e) {
          // Ignore errors when clearing
        }
      }

      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      };

      // Try to use back camera first (mobile), fallback to any camera
      let cameraId = null;
      try {
        const cameras = await Html5Qrcode.getCameras();
        // Prefer back camera on mobile devices
        const backCamera = cameras.find(camera => 
          camera.label.toLowerCase().includes('back') || 
          camera.label.toLowerCase().includes('rear') ||
          camera.label.toLowerCase().includes('environment')
        );
        cameraId = backCamera ? backCamera.id : cameras[0]?.id;
      } catch (err) {
        console.log("Could not get camera list, using default");
      }

      await html5QrCode.start(
        cameraId || { facingMode: "environment" },
        config,
        onScanSuccessHandler,
        (errorMessage) => {
          // Ignore scanning errors (they're frequent during scanning)
        }
      );

      setScanning(true);
    } catch (err) {
      console.error("Error starting scanner:", err);
      setScanning(false);
      toast.error("Unable to access camera. Please check permissions.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        await html5QrCodeRef.current.clear();
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const onScanSuccessHandler = async (decodedText) => {
    // Prevent multiple simultaneous processing
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    
    // Stop scanning temporarily while processing
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }

    // Process the scanned QR code
    await processQRToken(decodedText);
    
    setIsProcessing(false);
  };

  const handleManualInput = async (token) => {
    if (!token || token.trim() === "") {
      setError("Please enter a QR code token");
      return;
    }
    await processQRToken(token.trim());
  };

  const processQRToken = async (token) => {
    try {
      setError(null);
      // First verify the token
      const verifyResponse = await verifyQRTokenApi(token);
      
      if (!verifyResponse.data.valid) {
        throw new Error(verifyResponse.data.error || "Invalid QR code");
      }

      const invitation = verifyResponse.data.invitation;
      
      if (verifyResponse.data.already_checked_in) {
        setError("This candidate has already checked in");
        toast.warning("Already checked in", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        setScannedData({
          invitation,
          already_checked_in: true,
        });
        return;
      }

      // If valid and not checked in, proceed with check-in
      const checkInResponse = await checkInQRTokenApi(token);
      
      setScannedData({
        invitation,
        checked_in: checkInResponse.data.checked_in,
        duplicate: checkInResponse.data.duplicate,
      });

      toast.success("Check-in successful!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
      
      if (onScanSuccess) {
        onScanSuccess({ token, invitation });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.response?.data?.error || err.message || "Invalid QR code";
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
  };

  const handleClose = () => {
    stopScanner();
    setScannedData(null);
    setError(null);
    setManualInputValue("");
    setIsProcessing(false);
    // Clear input field
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    onCancel();
  };

  const handleScanAnother = async () => {
    setScannedData(null);
    setError(null);
    setManualInputValue("");
    setIsProcessing(false);
    // Clear input field
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    // Restart scanner
    if (html5QrCodeRef.current) {
      await stopScanner();
      await startScanner();
    }
  };

  return (
    <Modal
      title="QR Code Scanner"
      open={open}
      onCancel={handleClose}
      footer={null}
      width="95%"
      centered
      style={{ maxWidth: "600px" }}
      closeIcon={
        <span 
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            background: "#fee2e2",
            color: "#dc2626",
            cursor: "pointer",
            transition: "all 0.2s ease",
            fontSize: "18px",
            fontWeight: "600"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#fecaca";
            e.currentTarget.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#fee2e2";
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <i className='la la-close' />
        </span>
      }
    >
      <div style={{ padding: "20px 0" }}>
        {/* Manual Input Option */}
        <div className="mb-4">
          <label className="form-label" style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
            Manual Entry (Alternative)
          </label>
          <div className="input-group">
            <input
              ref={inputRef}
              type="text"
              className="form-control"
              placeholder="Enter QR code token manually"
              id="manual-qr-input"
              value={manualInputValue}
              onChange={(e) => setManualInputValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleManualInput(e.target.value);
                }
              }}
            />
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => {
                handleManualInput(manualInputValue);
              }}
              disabled={!manualInputValue || manualInputValue.trim().length !== 6}
            >
              Verify
            </button>
          </div>
        </div>

        {/* Camera View */}
        <div style={{ position: "relative", marginBottom: "20px" }}>
          <div
            id="qr-reader"
            style={{
              width: "100%",
              minHeight: "300px",
            }}
            className="qr-reader-responsive"
          />
          {!scanning && !scannedData && (
            <div style={{ 
              position: "absolute", 
              top: "50%", 
              left: "50%", 
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              color: "#666"
            }}>
              <i className="fa fa-camera fa-3x mb-2" style={{ display: "block" }}></i>
              <p>Starting camera...</p>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <i className="fa fa-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

        {/* Scanned Data Display */}
        {scannedData && (
          <div className={`alert ${scannedData.already_checked_in ? "alert-warning" : "alert-success"}`} role="alert">
            <h6 className="alert-heading">
              <i className={`fa ${scannedData.already_checked_in ? "fa-exclamation-triangle" : "fa-check-circle"} me-2`}></i>
              {scannedData.already_checked_in ? "Already Checked In" : "Candidate Verified"}
            </h6>
            <hr />
            {scannedData.invitation?.applicant && (
              <>
                <p className="mb-1">
                  <strong>Name:</strong> {scannedData.invitation.applicant.first_name} {scannedData.invitation.applicant.last_name}
                </p>
                <p className="mb-1">
                  <strong>Email:</strong> {scannedData.invitation.applicant.email || scannedData.invitation.email_to}
                </p>
              </>
            )}
            {scannedData.invitation?.slot && (
              <p className="mb-1">
                <strong>Time Slot:</strong> {scannedData.invitation.slot.title || ""} - {format(parseISO(scannedData.invitation.slot.start_at), "dd-MM-yyyy HH:mm")} - {format(parseISO(scannedData.invitation.slot.end_at), "HH:mm")}
              </p>
            )}
            {scannedData.invitation?.checked_in_at && (
              <p className="mb-1">
                <strong>Checked In At:</strong> {format(parseISO(scannedData.invitation.checked_in_at), "dd-MM-yyyy HH:mm")}
              </p>
            )}
            <p className="mb-3">
              <strong>Status:</strong> <span className={`badge ${scannedData.already_checked_in ? "bg-warning" : "bg-success"}`}>
                {scannedData.already_checked_in ? "Already Checked In" : scannedData.checked_in ? "Checked In" : "Verified"}
              </span>
            </p>
            <div className="d-flex gap-2">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={handleScanAnother}
              >
                <i className="fa fa-refresh me-2"></i> Scan Another
              </button>
              <button
                className="btn btn-primary"
                type="button"
                onClick={handleClose}
              >
                <i className="fa fa-times me-2"></i> Close
              </button>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-3">
          <small className="text-muted">
            <i className="fa fa-info-circle me-1"></i>
            Point the camera at the QR code or enter the token manually above.
          </small>
        </div>
      </div>
      <style>{`
        @media (max-width: 767.98px) {
          .qr-reader-responsive {
            min-height: 250px !important;
          }
          .ant-modal {
            margin: 10px !important;
          }
        }
      `}</style>
    </Modal>
  );
};

export default QRScanner;

